import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Pipette, RotateCcw, Trash2 } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'

interface AppDesignColorProps {
  isEditMode: boolean
  isSaving: boolean
  accentColor: string | null | undefined
  onColorChange: (hex: string) => void
  onResetClick: () => void
  onDeleteClick: () => void
}

export function AppDesignColor({
  isEditMode,
  isSaving,
  accentColor,
  onColorChange,
  onResetClick,
  onDeleteClick,
}: AppDesignColorProps) {
  const { t } = useTranslation(['configuration', 'common'])
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold">{t('color.title')}</p>

      <div className="flex items-center gap-3">
        <div
          className="h-10 w-10 rounded-md border shrink-0"
          style={{ backgroundColor: accentColor ?? 'transparent' }}
        />
        {accentColor ? (
          <span className="text-sm font-mono">{accentColor}</span>
        ) : (
          <span className="text-sm text-muted-foreground">{t('color.noColor')}</span>
        )}
      </div>

      {isEditMode && !isSaving && (
        <div className="flex items-center gap-1">
          <input
            ref={inputRef}
            type="color"
            className="sr-only"
            value={accentColor ?? '#000000'}
            onChange={(e) => onColorChange(e.target.value)}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={() => inputRef.current?.click()}>
                <Pipette className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('color.pick')}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={onResetClick}>
                <RotateCcw className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('buttons.reset', { ns: 'common' })}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={onDeleteClick} className="text-destructive hover:text-destructive">
                <Trash2 className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('buttons.delete', { ns: 'common' })}</TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  )
}
